/**
 * MailSenderAllowlist Component
 *
 * Senders the account trusts unconditionally — their mail is never quarantined, even when the
 * sending domain's published policy fails.
 */
import {
  Button,
  Icon,
  Input,
  InputWrapper,
  Paper,
  SectionLabel,
  SimpleTable
} from '@insio/ui-toolkit';
import { useFormatShortDate } from '@/hooks/use-date-formatters';
import { useMailSenderAllowlist } from '../../../hooks/use-mail-sender-allowlist';

export function MailSenderAllowlist() {
  const {
    t,
    entries,
    loading,
    error,
    value,
    validationError,
    handleValueChange,
    handleAdd,
    handleRemove,
    hasMore,
    loadMore,
    isBusy
  } = useMailSenderAllowlist();
  const formatShortDate = useFormatShortDate();

  type Entry = (typeof entries)[number];

  return (
    <Paper showDivider={false}>
      <Paper.Content className='space-y-md -m-lg'>
        <SectionLabel>{t('allowlist.title')}</SectionLabel>
        <p className='text-sm text-foreground-muted'>{t('allowlist.description')}</p>

        <div className='flex gap-sm rounded-lg bg-info-bg px-md py-sm'>
          <Icon name='circle-info' size='sm' className='text-info-text mt-2xs shrink-0' />
          <p className='text-sm text-info-text'>{t('allowlist.forwardingHint')}</p>
        </div>

        <InputWrapper
          label={t('allowlist.fields.value')}
          htmlFor='allowlist-value'
          labelSize='sm'
          hint={!validationError ? t('allowlist.fields.valueHint') : undefined}
        >
          <div className='flex items-start gap-sm'>
            <div className='flex-1'>
              <Input
                id='allowlist-value'
                value={value}
                onChange={handleValueChange}
                placeholder={t('allowlist.placeholders.value')}
                type='text'
                autoComplete='off'
                hasError={!!validationError}
                leftContent={<Icon name='envelope' size='sm' />}
              />
            </div>
            <Button
              variant='primary'
              onClick={() => void handleAdd()}
              disabled={isBusy || !value.trim()}
              testId='allowlist-add'
            >
              {t('allowlist.actions.add')}
            </Button>
          </div>
          {validationError && <p className='text-sm text-error-text mt-sm'>{validationError}</p>}
        </InputWrapper>

        {error ? (
          <p className='text-sm text-error-text'>{t('allowlist.error')}</p>
        ) : (
          <SimpleTable<Entry>
            headers={[
              t('allowlist.fields.value'),
              t('allowlist.fields.addedAt'),
              <span key='actions-header' className='flex justify-center'>
                {t('allowlist.fields.actions')}
              </span>
            ]}
            data={entries}
            NoContentFallback={
              <p className='text-sm text-foreground-muted'>
                {loading ? t('allowlist.loading') : t('allowlist.empty')}
              </p>
            }
            renderRow={entry => [
              <code
                key='value'
                className='text-xs bg-background-input px-sm py-xs rounded font-mono'
              >
                {entry.value}
              </code>,
              <span key='added' className='text-xs md:text-sm text-foreground-base'>
                {formatShortDate(entry.createdAt)}
              </span>,
              <div key='actions' className='flex justify-center'>
                <Button
                  variant='ghost'
                  onClick={() => void handleRemove(entry.id)}
                  disabled={isBusy}
                  testId={`allowlist-remove-${entry.value}`}
                >
                  {t('allowlist.actions.remove')}
                </Button>
              </div>
            ]}
          />
        )}

        {hasMore && (
          <div className='flex justify-center'>
            <Button
              variant='ghost'
              onClick={() => void loadMore()}
              disabled={isBusy || loading}
              testId='allowlist-load-more'
            >
              {t('allowlist.actions.loadMore')}
            </Button>
          </div>
        )}
      </Paper.Content>
    </Paper>
  );
}
